import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Psychology,
  TrendingUp,
  Work,
  SmartToy,
  CheckCircle,
  Warning,
  Lightbulb,
  School,
  Timeline,
  AutoAwesome,
  RocketLaunch,
  BusinessCenter
} from '@mui/icons-material';

const CareerLandingPage = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  // Redirect authenticated users to conversation
  useEffect(() => {
    if (session?.user) {
      navigate('/conversation');
    }
  }, [session, navigate]);

  const handleGetStarted = () => {
    navigate('/login?mode=signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <SmartToy sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: "AI-Proof Your Career",
      description: "Identify which skills AI can't replace and develop unshakeable career security. Stay ahead of automation with strategic career planning.",
      gradient: "linear-gradient(135deg, #2196F3, #21CBF3)"
    },
    {
      icon: <RocketLaunch sx={{ fontSize: 40, color: '#FF9800' }} />,
      title: "Career Transition Mastery",
      description: "Navigate career changes with confidence. Whether switching industries or climbing the ladder, get personalized roadmaps for success.",
      gradient: "linear-gradient(135deg, #FF9800, #FFB300)"
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: '#4CAF50' }} />,
      title: "Future-Ready Skills",
      description: "Build skills that thrive in an AI world: emotional intelligence, creative problem-solving, and human connection mastery.",
      gradient: "linear-gradient(135deg, #4CAF50, #66BB6A)"
    }
  ];

  const careerChallenges = [
    "Navigate AI disruption in your industry",
    "Build skills that can't be automated",
    "Transition to a fulfilling new career",
    "Overcome imposter syndrome and self-doubt",
    "Negotiate salary and promotions confidently",
    "Create work-life balance that actually works"
  ];

  const adaptationStories = [
    {
      name: "Alex Chen",
      previousRole: "Graphic Designer",
      newRole: "UX Strategy Director",
      challenge: "AI design tools threatening job",
      outcome: "300% salary increase",
      timeframe: "8 months",
      aiThreat: "High",
      rating: 5,
      story: "When AI started creating logos in seconds, I thought my career was over. The program helped me pivot to UX strategy where human psychology is irreplaceable. Now I lead AI tool implementation!"
    },
    {
      name: "Rebecca Martinez",
      previousRole: "Data Analyst", 
      newRole: "AI Ethics Consultant",
      challenge: "Role being automated",
      outcome: "Founded consulting firm",
      timeframe: "6 months",
      aiThreat: "Critical",
      rating: 5,
      story: "My entire department was replaced by AI. Instead of panicking, I learned about AI ethics and bias detection. Now companies pay me $200/hr to ensure their AI is responsible."
    },
    {
      name: "Marcus Thompson",
      previousRole: "Accountant",
      newRole: "Business Strategy Coach",
      challenge: "Accounting software evolution",
      outcome: "6-figure coaching business",
      timeframe: "12 months",
      aiThreat: "Medium",
      rating: 5,
      story: "AI handles the number crunching now, but businesses still need strategic thinking. I transformed my analytical skills into business coaching and tripled my income."
    }
  ];

  const aiDisruptionLevels = [
    { 
      level: "Critical Risk",
      description: "Jobs likely to be automated in 2-5 years",
      examples: "Data entry, basic analysis, routine coding",
      color: "#f44336",
      percentage: 35
    },
    { 
      level: "High Risk", 
      description: "Significant changes needed within 5-10 years",
      examples: "Traditional design, standard accounting, basic writing",
      color: "#ff9800",
      percentage: 45
    },
    { 
      level: "Evolution Required",
      description: "Must adapt and specialize to stay relevant",
      examples: "Management, sales, consulting, education",
      color: "#2196f3",
      percentage: 65
    },
    { 
      level: "Future-Proof",
      description: "Human skills that AI enhances rather than replaces",
      examples: "Therapy, strategy, creative leadership, innovation",
      color: "#4caf50",
      percentage: 85
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Professional Animated Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.1,
        background: `
          radial-gradient(circle at 10% 10%, #1976D2 0%, transparent 50%),
          radial-gradient(circle at 90% 90%, #424242 0%, transparent 50%),
          radial-gradient(circle at 70% 20%, #FFA726 0%, transparent 50%),
          radial-gradient(circle at 30% 80%, #66BB6A 0%, transparent 50%)
        `
      }} />

      {/* Header - Same as homepage */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 10,
        py: { xs: 2, md: 3 },
        px: { xs: 2, md: 4 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/sanity_logo.png" alt="Sanity Logo" style={{ 
              width: 48, 
              height: 48
            }} />
            <Typography variant="h5" sx={{ 
              fontWeight: 'bold', 
              color: 'white',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}>
              Sanity Reps
            </Typography>
          </Box>

          {/* Auth Buttons */}
          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 } }}>
            <Button 
              variant="outlined" 
              onClick={handleLogin}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                px: { xs: 2, md: 3 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': { 
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Login
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGetStarted}
              sx={{ 
                bgcolor: '#2196F3', 
                color: 'white',
                fontWeight: 'bold',
                px: { xs: 2, md: 3 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': { 
                  bgcolor: '#1976D2',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Future-Proof My Career
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10, pt: { xs: 4, md: 8 } }}>
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mb: 3, 
                    bgcolor: 'rgba(255,193,7,0.15)', 
                    color: 'white',
                    border: '1px solid rgba(255,193,7,0.3)',
                    '& .MuiAlert-icon': { color: '#FFC107' }
                  }}
                >
                  🚨 85% of jobs will be transformed by AI in the next decade
                </Alert>
                
                <Typography variant="h1" sx={{ 
                  fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 3,
                  lineHeight: 1.1,
                  textShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}>
                  Thrive in the 
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(45deg, #2196F3, #00BCD4)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {' '}AI Era
                  </Box>
                </Typography>
                
                <Typography variant="h4" sx={{ 
                  fontSize: { xs: '1.25rem', md: '1.75rem' },
                  color: 'rgba(255,255,255,0.9)',
                  mb: 4,
                  fontWeight: 300,
                  lineHeight: 1.4
                }}>
                  Don't let AI replace you. Learn to work with AI, build irreplaceable human skills, and create a future-proof career that thrives.
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <List sx={{ color: 'white' }}>
                    {careerChallenges.slice(0, 3).map((challenge, index) => (
                      <ListItem key={index} sx={{ pl: 0 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: '#2196F3' }} />
                        </ListItemIcon>
                        <ListItemText primary={challenge} sx={{ '& .MuiTypography-root': { fontSize: '1.1rem' } }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleGetStarted}
                    sx={{ 
                      background: 'linear-gradient(45deg, #2196F3, #FF9800)',
                      color: 'white',
                      px: 4,
                      py: 2,
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      borderRadius: 3,
                      '&:hover': { 
                        background: 'linear-gradient(45deg, #1976D2, #F57C00)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 10px 30px rgba(33, 150, 243, 0.4)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🚀 Secure My Future Now
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank')}
                    sx={{ 
                      color: 'white',
                      borderColor: 'white',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: 3,
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white'
                      }
                    }}
                  >
                    AI Career Assessment
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Fade in timeout={1500}>
              <Box sx={{ position: 'relative' }}>
                <Card sx={{ 
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.2)',
                  p: 3
                }}>
                  <Typography variant="h5" sx={{ color: 'white', mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
                    AI Disruption Risk Levels
                  </Typography>
                  
                  {aiDisruptionLevels.map((level, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>
                          {level.level}
                        </Typography>
                        <Typography sx={{ color: level.color, fontWeight: 'bold' }}>
                          {level.percentage}% secure
                        </Typography>
                      </Box>
                      
                      <Box sx={{ position: 'relative', mb: 1 }}>
                        <Box sx={{ 
                          height: 8, 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          borderRadius: 4,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            height: '100%', 
                            width: `${level.percentage}%`,
                            bgcolor: level.color,
                            borderRadius: 4,
                            transition: 'width 2s ease-in-out'
                          }} />
                        </Box>
                      </Box>
                      
                      <Typography sx={{ 
                        color: 'rgba(255,255,255,0.8)', 
                        fontSize: '0.8rem',
                        mb: 1
                      }}>
                        {level.description}
                      </Typography>
                      
                      <Typography sx={{ 
                        color: 'rgba(255,255,255,0.6)', 
                        fontSize: '0.75rem',
                        fontStyle: 'italic'
                      }}>
                        Examples: {level.examples}
                      </Typography>
                    </Box>
                  ))}
                </Card>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 10 }}>
        <Typography variant="h2" sx={{ 
          textAlign: 'center', 
          mb: 6, 
          fontWeight: 'bold',
          color: 'white',
          fontSize: { xs: '2rem', md: '3rem' }
        }}>
          Your Career Future-Proofing Toolkit
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Fade in timeout={1000 + index * 200}>
                <Card sx={{ 
                  height: '100%',
                  background: feature.gradient,
                  color: 'white',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                  }
                }}>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Adaptation Stories Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 10 }}>
        <Typography variant="h2" sx={{ 
          textAlign: 'center', 
          mb: 6, 
          fontWeight: 'bold',
          color: 'white',
          fontSize: { xs: '2rem', md: '3rem' }
        }}>
          AI Career Adaptation Success Stories
        </Typography>
        
        <Grid container spacing={4}>
          {adaptationStories.map((story, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Fade in timeout={1000 + index * 300}>
                <Card sx={{ 
                  height: '100%',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    transform: 'translateY(-5px)',
                    background: 'rgba(255,255,255,0.15)'
                  }
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {story.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                        {story.previousRole} → {story.newRole}
                      </Typography>
                      
                      <Chip 
                        label={`AI Threat: ${story.aiThreat}`}
                        sx={{ 
                          mt: 1,
                          bgcolor: story.aiThreat === 'Critical' ? '#f44336' : 
                                   story.aiThreat === 'High' ? '#ff9800' : '#2196f3',
                          color: 'white',
                          fontSize: '0.75rem'
                        }} 
                      />
                    </Box>
                    
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                        Challenge: {story.challenge}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold', mb: 1 }}>
                        Result: {story.outcome}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FF9800' }}>
                        Transformation time: {story.timeframe}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body1" sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      textAlign: 'center',
                      fontSize: '0.9rem'
                    }}>
                      "{story.story}"
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 10 }}>
        <Paper sx={{ 
          background: 'linear-gradient(135deg, #2196F3, #1976D2)',
          color: 'white',
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
        }}>
          <Box sx={{ fontSize: '3rem', mb: 2 }}>🤖</Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 'bold',
            fontSize: { xs: '1.75rem', md: '2.5rem' }
          }}>
            Don't Get Left Behind by AI
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            The future belongs to those who adapt now. Get your personalized AI career strategy and stay ahead of the curve.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={handleGetStarted}
            sx={{ 
              bgcolor: 'white',
              color: '#2196F3',
              px: 4,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              borderRadius: 3,
              '&:hover': { 
                bgcolor: '#f5f5f5',
                transform: 'translateY(-3px)',
                boxShadow: '0 10px 30px rgba(255,255,255,0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            🚀 Future-Proof My Career Now
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default CareerLandingPage;
